'use client';

import { Button, Checkbox, Divider, Modal } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgInfo, SvgShare2 } from '@common/icon';
import { cn } from '@core/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import {
  AuthMutation,
  AuthQueryOptions,
  TERM_CATEGORY,
  VERIFICATION_TYPE,
} from '@/api';
import { DesktopSideAdBanner } from '@/app/_components/ads';
import { CustomLink } from '@/app/_components/CustomLink';
import { BottomSheet } from '@/app/welmarket/_components/BottomSheet';
import { Image } from '@/components/client';
import { IMAGE_PLACEHOLDER, ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';
import { useGlobalStore } from '@/stores/GlobalStore';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';

import { PointUsageSection } from './PointUsageSection';

interface ProductDetail {
  thumbnailUrl?: string;
  productName?: string;
  brandName?: string;
  originalPrice?: number;
  brandNotice?: string;
  productType?: string;
}

interface ProductDetailClientProps {
  idx: string;
  product: ProductDetail;
}

export const ProductDetailClient = ({
  idx,
  product,
}: ProductDetailClientProps) => {
  const {
    thumbnailUrl,
    productName = '',
    brandName,
    originalPrice = 0,
    brandNotice,
    productType,
  } = product;

  const isLogin = useAuthStore((state) => state.isLogin);
  const myInfo = useAuthStore((state) => state.myInfo);
  const setShareLink = useGlobalStore((state) => state.setShareLink);
  const setTermsDrawerType = useGlobalStore(
    (state) => state.setTermsDrawerType,
  );

  const router = useCustomRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();

  const previewQuery = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/point-market/order/preview']({
      query: { productIdIdx: idx },
    }),
    enabled: isLogin === true,
    select: ({ context }) => context,
  });

  const isAuthResolving = isLogin === undefined;
  const isPreviewLoading = isLogin === true && previewQuery.isLoading;
  const availablePoints = previewQuery.data?.point.availablePoint ?? 0;
  const usedPoints = previewQuery.data?.point.usedPoint ?? originalPrice;
  const leftPoints = availablePoints - usedPoints;
  const isPointsInsufficient = isLogin === true && leftPoints < 0;

  const [isCheckedExp, setIsCheckedExp] = useState(false);
  const [isCheckedPhone, setIsCheckedPhone] = useState(false);
  const [isCheckedMms, setIsCheckedMms] = useState(false);
  const [isCheckedPrivacy, setIsCheckedPrivacy] = useState(false);
  const isAllChecked =
    isCheckedExp && isCheckedPhone && isCheckedMms && isCheckedPrivacy;

  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);

  const { mutate: pay, isPending: isPaying } = useMutation({
    ...AuthMutation['post:/wello/v2/auth/point-market/order/payments'](),
    onSuccess: async () => {
      trackGtmEvent(GTM_EVENT_TYPE.USE_POINT, {
        product_name: productName,
        product_brand: brandName ?? '',
        product_value: originalPrice,
        product_id: idx,
      });
      await queryClient.invalidateQueries();
      toast({ message: '구매하신 교환권이 문자로 발송되었어요.' });
      router.replace(ROUTES.POINT_MARKET_ORDER_COMPLETE.pathname);
    },
    onError: (error) => {
      toastError({ error });
    },
  });

  const verificationHref = ROUTES.EASY_CERTIFICATION.withSearchParams({
    searchParams: {
      verification: VERIFICATION_TYPE.PROFILE_UPDATE,
      isNotUseCertificateCache: 'Y',
      redirect: pathname ?? '',
    },
  });

  const handlePaymentClick = () => {
    if (isAuthResolving || isPreviewLoading) return;

    if (!isLogin) {
      router.push(
        ROUTES.LOGIN.withSearchParams({
          searchParams: { redirect: pathname ?? '' },
        }),
      );

      return;
    }

    if (isPointsInsufficient) return;

    if ((myInfo?.cellPhone?.length ?? 0) < 5) {
      setIsVerifyModalOpen(true);

      return;
    }

    pay({ body: { productIdIdx: idx } });
  };

  const handleShare = () => {
    if (typeof window === 'undefined') return;
    setShareLink(window.location.href);
  };

  const ctaDisabled =
    isAuthResolving ||
    isPreviewLoading ||
    (isLogin === true && (!isAllChecked || isPointsInsufficient || isPaying));
  const ctaLabel = isPointsInsufficient
    ? '포인트가 부족해요'
    : '결제하기';

  const safeThumbnail = thumbnailUrl || IMAGE_PLACEHOLDER;
  const formattedPhone = myInfo?.cellPhone
    ? myInfo.cellPhone.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
    : null;

  return (
    <article className="mx-auto w-full max-w-root pt-100 mobile-tablet:pt-0">
      <div
        className={cn(
          'grid grid-cols-[660px_480px] justify-between gap-x-60',
          'mobile-tablet:grid-cols-1 mobile-tablet:gap-x-0',
        )}
      >
        <section className="flex flex-col gap-48 mobile-tablet:gap-24 mobile-tablet:pb-180">
          <div className="relative aspect-square w-full overflow-hidden rounded-large mobile-tablet:rounded-none">
            <Image
              fill
              sizes="(max-width: 1279px) 100vw, 660px"
              src={safeThumbnail}
              alt={productName}
              className="object-cover"
            />
          </div>

          <section className="hidden flex-col gap-16 px-20 mobile-tablet:flex">
            <div className="flex items-start justify-between gap-12">
              <div className="flex flex-1 flex-col gap-8">
                {brandName ? (
                  <div className="flex h-22 w-fit items-center justify-center rounded-pill bg-Gray-200 px-6 py-3">
                    <span className="font-600 text-Gray-600 typo-caption2">
                      {brandName}
                    </span>
                  </div>
                ) : null}
                <h1 className="font-700 text-Gray-900 typo-h7">
                  {productName}
                </h1>
              </div>
              <button
                type="button"
                aria-label="상품 공유"
                onClick={handleShare}
                className="size-20 shrink-0 text-Gray-900"
              >
                <SvgShare2 className="size-full" />
              </button>
            </div>

            <p className="font-700 text-Gray-700 typo-h5">
              {originalPrice.toLocaleString()}P
            </p>
          </section>

          <Divider />

          <section className="hidden px-20 mobile-tablet:block">
            <dl className="flex flex-col gap-12">
              <div className="flex gap-16">
                <dt className="flex w-92 items-center gap-4 font-400 text-Gray-600 typo-body4">
                  배송비
                  <SvgInfo className="size-12 text-Gray-500" />
                </dt>
                <dd className="font-700 text-Gray-900 typo-body4">무료배송</dd>
              </div>
              <div className="flex gap-16">
                <dt className="w-92 font-400 text-Gray-600 typo-body4">
                  배송안내
                </dt>
                <dd className="font-700 text-Gray-900 typo-body4">
                  구매 후 바로 온라인 발급
                </dd>
              </div>
            </dl>
          </section>

          <Divider />

          <dl className="flex flex-col gap-28 mobile-tablet:gap-24 mobile-tablet:px-20">
            <div className="flex flex-col gap-8">
              <dt className="font-600 text-Gray-900 typo-body1 mobile-tablet:typo-body3">
                상품명
              </dt>
              <dd className="font-400 leading-normal text-Gray-600 typo-body3 mobile-tablet:typo-body4">
                {productName}
              </dd>
            </div>
            {productType ? (
              <div className="flex flex-col gap-8">
                <dt className="font-600 text-Gray-900 typo-body1 mobile-tablet:typo-body3">
                  상품유형
                </dt>
                <dd className="font-400 leading-normal text-Gray-600 typo-body3 mobile-tablet:typo-body4">
                  {productType}
                </dd>
              </div>
            ) : null}
            {brandNotice ? (
              <div className="flex flex-col gap-8">
                <dt className="font-600 text-Gray-900 typo-body1 mobile-tablet:typo-body3">
                  상품 정보
                </dt>
                <dd className="whitespace-pre-wrap font-400 leading-normal text-Gray-600 typo-body3 mobile-tablet:typo-body4">
                  {brandNotice}
                </dd>
              </div>
            ) : null}
          </dl>

          <Divider />

          <div className="mobile-tablet:hidden">
            <PointUsageSection
              availablePoints={availablePoints}
              usedPoints={usedPoints}
              isInsufficient={isPointsInsufficient}
            />
          </div>

          <Divider className="mobile-tablet:hidden" />

          <section className="flex flex-col gap-16 mobile-tablet:px-20">
            <h2 className="font-700 text-Gray-900 typo-h6 mobile-tablet:typo-body2">
              결제 금액
            </h2>
            <dl className="flex flex-col gap-12 typo-body3">
              <div className="flex items-center justify-between">
                <dt className="font-700 text-Gray-900">보유 웰포인트</dt>
                <dd className="font-700 text-Gray-900">
                  {availablePoints.toLocaleString()}P
                </dd>
              </div>
              <div className="flex items-center justify-between pl-4">
                <dt className="font-500 text-Gray-600">상품 금액</dt>
                <dd className="font-500 text-Gray-600">
                  {usedPoints > 0 && '-'}
                  {usedPoints.toLocaleString()}P
                </dd>
              </div>
              <Divider />
              <div className="flex items-center justify-between font-700 text-Gray-900 typo-h6">
                <dt>잔여 웰포인트</dt>
                <dd
                  className={cn(
                    leftPoints < 0 ? 'text-R-400' : 'text-Gray-900',
                  )}
                >
                  {leftPoints.toLocaleString()}P
                </dd>
              </div>
            </dl>
          </section>

          <div className="bg-Gray-50 p-20 font-600 leading-normal text-Gray-600 typo-body4 mobile-tablet:mx-20">
            <span className="font-700 text-O-400">
              {formattedPhone ? `[${formattedPhone}]` : '[휴대전화번호 변수]'}
            </span>
            로 MMS 메시지를 통해 모바일 쿠폰을 발송합니다. 휴대전화 번호가 변경
            되었거나 일치하지 않는 경우{' '}
            <CustomLink className="underline" href={verificationHref}>
              여기
            </CustomLink>
            를 눌러 본인 인증을 다시 진행해 주세요. 인증이 완료되면 해당 번호로
            프로필 정보가 자동 업데이트 됩니다.
          </div>

          <section className="flex flex-col gap-12 pb-40 mobile-tablet:px-20">
            <p className="text-Gray-800 typo-body4">
              웰 포인트상점 모바일쿠폰 발급 이용 약관
            </p>
            <div className="rounded-xsmall bg-Gray-100 px-14 py-12">
              <Checkbox
                className="gap-6 text-Gray-600 typo-caption1"
                checked={isAllChecked}
                onCheckedChange={(checked) => {
                  setIsCheckedExp(checked);
                  setIsCheckedPhone(checked);
                  setIsCheckedMms(checked);
                  setIsCheckedPrivacy(checked);
                }}
              >
                <strong className="font-600 text-Gray-black typo-body4">
                  약관 전체 동의
                </strong>
              </Checkbox>
            </div>
            <div className="mt-4 flex flex-col gap-6">
              <Checkbox
                className="text-Gray-900 typo-caption1"
                checked={isCheckedExp}
                onCheckedChange={(checked) => setIsCheckedExp(checked)}
              >
                만료 기한 연장이 불가능한 모바일 쿠폰임을 확인했습니다.
              </Checkbox>
              <Checkbox
                className="text-Gray-900 typo-caption1"
                checked={isCheckedPhone}
                onCheckedChange={(checked) => setIsCheckedPhone(checked)}
              >
                전화번호 변경 및 기타 어떤 사유로도 재발급이 불가능합니다.
              </Checkbox>
              <Checkbox
                className="text-Gray-900 typo-caption1"
                checked={isCheckedMms}
                onCheckedChange={(checked) => setIsCheckedMms(checked)}
              >
                MMS 메시지 수신이 가능한 상태임을 확인했습니다.
              </Checkbox>
              <Checkbox
                className="text-Gray-900 typo-caption1"
                checked={isCheckedPrivacy}
                onCheckedChange={(checked) => setIsCheckedPrivacy(checked)}
              >
                <div>
                  회원 본인은 위 주문 내용을 확인하였으며 서비스 약관, 개인정보
                  수집동의, 개인정보 제 3자 제공동의(웰로)에 동의합니다.{' '}
                  <button
                    type="button"
                    className="font-700 text-Gray-black underline typo-caption1"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setTermsDrawerType(
                        TERM_CATEGORY.DAOU_PROVIDE_PRIVACY_SELECT,
                      );
                    }}
                  >
                    보기
                  </button>
                </div>
              </Checkbox>
            </div>
          </section>
        </section>

        <div className="flex flex-col gap-40 mobile-tablet:hidden">
          <aside className="py-32">
            <div className="flex flex-col gap-40">
              <div className="flex flex-col gap-28">
                <div className="flex items-start justify-between gap-12">
                  <div className="flex flex-1 flex-col gap-8">
                    {brandName ? (
                      <div className="self-start bg-Gray-200 px-12 py-4">
                        <span className="font-600 tracking-[-0.28px] text-Gray-600 typo-body4">
                          {brandName}
                        </span>
                      </div>
                    ) : null}
                    <h1 className="font-800 leading-[1.2] text-Gray-900 typo-h4">
                      {productName}
                    </h1>
                  </div>
                  <button
                    type="button"
                    aria-label="상품 공유"
                    onClick={handleShare}
                    className="size-20 shrink-0 text-Gray-900"
                  >
                    <SvgShare2 className="size-full" />
                  </button>
                </div>

                <p className="font-700 leading-[1.2] text-Gray-700 typo-h3">
                  {originalPrice.toLocaleString()}P
                </p>

                <dl className="flex flex-col gap-16">
                  <div className="flex gap-56">
                    <dt className="flex w-92 items-center gap-4 font-500 leading-normal text-Gray-600 typo-body3">
                      배송비
                      <SvgInfo className="size-16 text-Gray-500" />
                    </dt>
                    <dd className="font-700 leading-normal text-Gray-900 typo-body3">
                      무료배송
                    </dd>
                  </div>
                  <div className="flex gap-56">
                    <dt className="w-92 font-500 leading-normal text-Gray-600 typo-body3">
                      배송안내
                    </dt>
                    <dd className="flex flex-col leading-normal">
                      <span className="font-700 text-Gray-900 typo-body3">
                        구매 후 바로 온라인 발급
                      </span>
                      <span className="font-500 text-Gray-700 typo-body3">
                        (식품의 경우 지연 발생 시 1주~2주 소요)
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="flex flex-col gap-20">
                <div className="flex items-center justify-between gap-16">
                  <span className="flex-1 font-700 leading-[1.2] text-Gray-900 typo-h5">
                    상품 금액
                  </span>
                  <span className="font-800 leading-[1.2] text-Gray-900 typo-h4">
                    {originalPrice.toLocaleString()}P
                  </span>
                </div>
                <button
                  type="button"
                  disabled={ctaDisabled}
                  onClick={handlePaymentClick}
                  className={cn(
                    'flex h-68 w-full items-center justify-center bg-O-400 px-24 py-8 font-700 text-Gray-white transition-colors typo-h6',
                    'disabled:bg-Gray-300 disabled:text-Gray-500',
                  )}
                >
                  {ctaLabel}
                </button>
              </div>
            </div>
          </aside>
          <DesktopSideAdBanner />
        </div>
      </div>

      <BottomSheet
        toast={
          <p className="font-700 typo-caption1">
            <span className="text-Gray-white">이 상품은</span>
            <span className="text-O-400"> 웰포인트로만 </span>
            <span className="text-Gray-white">구매가 가능해요!</span>
          </p>
        }
      >
        <div className="flex flex-col gap-12">
          <div className="flex items-center justify-between py-8">
            <span className="font-700 text-Gray-900 typo-body3">상품 금액</span>
            <span className="font-700 text-Gray-black typo-body1">
              {originalPrice.toLocaleString()}P
            </span>
          </div>
          <button
            type="button"
            disabled={ctaDisabled}
            onClick={handlePaymentClick}
            className={cn(
              'flex h-56 w-full items-center justify-center bg-O-400 font-700 text-Gray-white typo-body3',
              'disabled:bg-Gray-300 disabled:text-Gray-500',
            )}
          >
            {ctaLabel}
          </button>
        </div>
      </BottomSheet>

      <Modal
        opened={isVerifyModalOpen}
        onClose={() => setIsVerifyModalOpen(false)}
        header={
          <span className="w-full text-center mobile:typo-body1">
            모바일 쿠폰 발송을 위해
            <br />
            본인인증이 필요합니다
          </span>
        }
        description={
          <span className="inline-block w-full text-center">
            계속 하시겠습니까?
          </span>
        }
        buttonFooter={
          <div className="flex items-center gap-12">
            <Button
              theme="primary-fill"
              dimensions="H48"
              className="flex-1"
              onClick={() => {
                setIsVerifyModalOpen(false);
                router.push(verificationHref);
              }}
            >
              확인
            </Button>
            <Button
              theme="sub-line"
              dimensions="H48"
              className="flex-1"
              onClick={() => setIsVerifyModalOpen(false)}
            >
              취소
            </Button>
          </div>
        }
      />
    </article>
  );
};
